import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context';
import empresasService from 'services/empresas';
import { AlertaError, AlertaExito } from 'utils/Alertas';
import { DICTONARY } from 'const/Dictonary';
import { PERMISOS } from 'const/Permisos';
import { numeroFrontABaseDatos } from 'helpers/formateoNumeros';
import { useCargaPerfiles } from 'hooks/useCargaPerfiles';

export const useEditarIndicador = (codigoSucursal, codigoEmpresa) => {
  const {
    logeduser,
    refrescarSesion,
    token,
    isTienePermisos,
    sucursalNoAdministrada
  } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [sucursal, setSucursal] = useState(null);

  const { cargaPerfiles, permisos } = useCargaPerfiles();

  const formularioInicial = sucursalSeleccionada => {
    const roles = sucursalSeleccionada
      ? sucursalSeleccionada.empresa.roles.map(rol =>
          rol.nombreRol.replaceAll(' ', '')
        )
      : [DICTONARY.ROL.GENERADOR.NOMBRE];

    const initialValue = {
      base: {
        role: roles,
        trabajadoresMasculino: sucursalSeleccionada?.trabajadoresMasculino,
        trabajadoresFemenino: sucursalSeleccionada?.trabajadoresFemenino,
        consumoEnergetico: sucursalSeleccionada?.consumoEnergetico,
        consumoCombustible: sucursalSeleccionada?.consumoCombustible
      },
      generador: {
        residuosGenerados: sucursalSeleccionada?.residuosGenerados,
        residuosProyectados: sucursalSeleccionada?.residuosProyectados,
        procesoGestion: sucursalSeleccionada?.trabajadoresMasGestion
          ? true
          : false,
        trabajadoresMasGestion:
          sucursalSeleccionada?.trabajadoresMasGestion || '',
        trabajadoresFemGestion:
          sucursalSeleccionada?.trabajadoresFemGestion || '',
        totalHH: sucursalSeleccionada?.totalHH || '',
        remuneracionesPagadas: sucursalSeleccionada?.remuneracionesPagadas || ''
      },
      receptor: {
        residuosCompradosRecibidos:
          sucursalSeleccionada?.residuosCompradosRecibidos,
        procesoGestion: sucursalSeleccionada?.trabajadoresMasGestion
          ? true
          : false,
        trabajadoresMasGestion:
          sucursalSeleccionada?.trabajadoresMasGestion || '',
        trabajadoresFemGestion:
          sucursalSeleccionada?.trabajadoresFemGestion || '',
        totalHH: sucursalSeleccionada?.totalHH || '',
        remuneracionesPagadas: sucursalSeleccionada?.remuneracionesPagadas || ''
      },
      transportista: {},
      gestor: {},
      asesor: {},
      permisosNegocio: {}
    };

    // Verificar si el usuario tiene permisos de negocio
    // Validaciones de permisos de negocio
    /**
     *  - nombreVariable: [PERMISOS]
     */
    const permisosNegocio = {
      isTieneResiduoGenerado: PERMISOS.residuoGenerado,
      isTieneResiduoProyectado: PERMISOS.residuoProyectado,
      isTieneResiduoCompradoRecibido: PERMISOS.recibidoComprado,
      isTieneCapacidadProyectada: PERMISOS.totalProyectadoTransportar,
      isTieneResiduosTransportados: PERMISOS.totalTransportadas,
      isTieneKmRecorridos: PERMISOS.kmRecorridos,
      isTieneResiduosGestionar: PERMISOS.totalGestionar,
      isTieneResiduosGestionados: PERMISOS.totalGestionado,
      isTieneResiduosAsesorados: PERMISOS.totalAsesorado,
      isTieneProcesoGestion: PERMISOS.procesoGestion
    };

    Object.keys(permisosNegocio).forEach(key => {
      permisosNegocio[key] = permisos.some(
        permiso => permiso.codigoPermiso === permisosNegocio[key]
      );
    });

    /**
     * - nombreVariable: [seccion, campo]
     * * en caso de no estar en la lista de permisos de negocio, se asigna el valor de la sucursal
     */
    const asignaciones = {
      isTieneResiduoGenerado: ['generador', 'residuosGenerados'],
      isTieneResiduoProyectado: ['generador', 'residuosProyectados'],
      isTieneResiduoCompradoRecibido: [
        'receptor',
        'residuosCompradosRecibidos'
      ],
      isTieneCapacidadProyectada: ['transportista', 'capacidadProyectada'],
      isTieneResiduosTransportados: ['transportista', 'residuosTransportados'],
      isTieneKmRecorridos: ['transportista', 'kilometrosRecorridos'],
      isTieneResiduosGestionar: ['gestor', 'residuosGestionar'],
      isTieneResiduosGestionados: ['gestor', 'residuosGestionados'],
      isTieneResiduosAsesorados: ['asesor', 'residuosAsesorados']
    };

    Object.entries(asignaciones).forEach(([permiso, [seccion, campo]]) => {
      if (permisosNegocio[permiso]) {
        initialValue[seccion][campo] = sucursalSeleccionada?.[campo];
      }
    });

    initialValue.permisosNegocio = { permisosNegocio };

    try {
      const externalObj = {};
      roles.map(rol => {
        let data = initialValue[rol];
        Object.keys(data).forEach(key => {
          externalObj[key] = data[key];
        });
        return data;
      });

      return {
        ...initialValue.base,
        ...initialValue.permisosNegocio,
        ...externalObj
      };
    } catch (error) {
      return {
        ...initialValue.base,
        ...initialValue.permisosNegocio
      };
    }
  };

  useEffect(() => {
    /**
     * @name cargarSucursal
     * Función para cargar la sucursal que se está desplegando, en el caso de que el código no viene se asume que es una nueva sucursal.
     */

    const cargarSucursal = async () => {
      setIsLoading(true);
      if (codigoSucursal !== 'nuevo') {
        try {
          const responseSucursal = await empresasService.getSucursal(
            codigoSucursal
          );
          if (responseSucursal.data.error)
            throw new Error('ERROR_CREAR_REUNION');

          const sucursalSeleccionada = responseSucursal.data;
          setSucursal(sucursalSeleccionada);

          // Se extrae los roles y se cargan perfiles antes de continuar
          const roles = sucursalSeleccionada.empresa.roles.map(
            rol => rol.codigoRol
          );

          await cargaPerfiles(roles); // Espera a que los perfiles se carguen antes de continuar

          // Ahora que los perfiles están listos, inicializa el formulario
          formularioInicial(sucursalSeleccionada);
        } catch (error) {
          console.log('Error al cargar la sucursal', error);
          setSucursal(null);
        } finally {
          setIsLoading(false);
        }
      }
    };

    cargarSucursal();
  }, [codigoSucursal]); // Se ejecutará cuando `codigoSucursal` cambie

  const onSubmit = async (codigoSucursal, values) => {
    const cleanValues = { ...values };
    delete cleanValues.role;

    Object.keys(cleanValues).forEach(key => {
      if (typeof cleanValues[key] === 'string') {
        const numeroFormateado = numeroFrontABaseDatos(cleanValues[key]);
        cleanValues[key] = numeroFormateado;
      }
    });

    try {
      const response = await empresasService.actualizarIndicadores(
        codigoSucursal,
        cleanValues
      );
      if (codigoSucursal === sucursalNoAdministrada?.sucursal?.codigoSucursal) {
        // Si se está editando la sucursal no administrada
        await refrescarSesion(token);
      }
      const error = response.data.error;
      if (error) throw new Error('ERROR_AL_GUARDAR_EL_INDICADOR');
      AlertaExito('Se han guardado los cambios correctamente');
    } catch (error) {
      console.log(error);
      AlertaError(
        'Ha ocurrido un error al guardar los datos vuelve a intentarlo. Si el problema persiste comunicate con el administrador del sitio'
      );
    }
  };

  return {
    isLoading,
    sucursal,
    formularioInicial: formularioInicial,
    onSubmit: onSubmit
  };
};
